import moment from 'moment-timezone';

import { ROUTES } from '@/constants/routes';
import i18n from '@/i18n';

import type { FollowingEvent } from '@/components/following/helpers/sectionHelper';

import {
	BesoinFollowingType,
	buildFollowingLineBesoin,
} from '@/components/following/helpers/besoinFactory';
import {
	FollowingTypeDto,
	WorkerActivitySource,
	type WorkerBesoinHistoryEntryDto,
} from '@/components/following/types';

const EVENT_TYPE_TO_BESOIN_TYPE: Record<string, BesoinFollowingType> = {
	[FollowingTypeDto.PREQUALIFIED]: BesoinFollowingType.MOVED_TO_PRESELECTED,
	[FollowingTypeDto.CONTACTED]: BesoinFollowingType.MOVED_TO_CONTACTED,
	[FollowingTypeDto.INTERVIEW]: BesoinFollowingType.MOVED_TO_INTERVIEW_PLANNED,
	[FollowingTypeDto.COMPANY_PROPOSED]: BesoinFollowingType.MOVED_TO_PROPOSED,
	[FollowingTypeDto.COMPANY_INTERVIEW]:
		BesoinFollowingType.MOVED_TO_COMPANY_INTERVIEW,
	[FollowingTypeDto.MISSION_PROPOSED]:
		BesoinFollowingType.MOVED_TO_MISSION_PROPOSED,
	[FollowingTypeDto.REJECTED]: BesoinFollowingType.REFUSED,
};

const getUserNotSpecifiedLabel = (): string =>
	i18n.global.t('following.labels.userNotSpecified');

const formatDetail = (
	createdAt: string,
	userFirstname: string,
	userLastname: string
): string => {
	const date = moment(createdAt).format('HH:mm');
	const name =
		[userFirstname, userLastname].filter(Boolean).join(' ') ||
		getUserNotSpecifiedLabel();
	return `${date} - ${name}`;
};

export const mapWorkerBesoinHistoryEntryToFollowingEvent = (
	entry: WorkerBesoinHistoryEntryDto
): FollowingEvent => {
	const type =
		EVENT_TYPE_TO_BESOIN_TYPE[entry.eventType] ??
		BesoinFollowingType.MOVED_TO_PRESELECTED;
	const detail = formatDetail(
		entry.createdAt,
		entry.userFirstname,
		entry.userLastname
	);
	const displaysReadonlyComment =
		type === BesoinFollowingType.MOVED_TO_CONTACTED ||
		type === BesoinFollowingType.REFUSED;
	const trimmedComment = displaysReadonlyComment
		? (entry.comment?.trim() ?? '')
		: '';
	const line = buildFollowingLineBesoin({
		type,
		date: new Date(entry.date).getTime(),
		besoinName: entry.besoinName,
		besoinTo: {
			name: ROUTES.APP.COMPANY_NEED.DETAIL.MATCHING,
			params: { companyNeedId: entry.besoinId },
		},
		detail,
		...(trimmedComment ? { comment: trimmedComment } : {}),
	});
	const agencyId =
		typeof entry.agencyId === 'string' && entry.agencyId.trim()
			? entry.agencyId.trim()
			: undefined;

	return {
		id: entry.id,
		createdAt: new Date(entry.createdAt),
		line,
		source: WorkerActivitySource.BESOIN,
		...(agencyId ? { agencyId } : {}),
	};
};

export const workerBesoinHistoryEntriesToFollowingEvents = (
	entries: WorkerBesoinHistoryEntryDto[]
): FollowingEvent[] => entries.map(mapWorkerBesoinHistoryEntryToFollowingEvent);
